import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { sampleAttachmentsApi, type Sample, type StoredFile } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Dialog,
  FileDownloadButton,
  FileUploadField,
  Panel,
  TextAreaField,
} from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { formatBytes, formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';

/**
 * Sample photos and attachments — carried from Phase 7 (decision D80).
 *
 * AUTHORISED AS THE SAMPLE (D92). Every list, upload, download and removal
 * goes through the sample's own routes, which check `samples.view` — never
 * through the generic files API, which refuses these files outright.
 *
 * Attaching a photo records it. It does not mean anybody or anything examined
 * what it shows.
 */
export function SampleAttachmentsPanel({ sample }: { readonly sample: Sample }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const [removing, setRemoving] = useState<StoredFile | null>(null);
  const [reason, setReason] = useState('');

  const files = useQuery({
    queryKey: ['sample', sample.id, 'attachments'],
    queryFn: () => sampleAttachmentsApi.list(sample.id),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['sample', sample.id, 'attachments'] });

  const remove = useMutation({
    mutationFn: () => sampleAttachmentsApi.remove(sample.id, (removing as StoredFile).id, reason.trim()),
    onSuccess: async () => {
      await refresh();
      setRemoving(null);
      setReason('');
    },
  });

  // A voided sample takes no further work (D211): its files stay, read-only.
  const mayAttach =
    !sample.isVoided && can(PERMISSIONS.SAMPLES_UPDATE) && can(PERMISSIONS.FILES_UPLOAD);
  const mayRemove =
    !sample.isVoided && can(PERMISSIONS.SAMPLES_UPDATE) && can(PERMISSIONS.FILES_DELETE);

  return (
    <Panel title={t('sampleAttachments.title')} description={t('sampleAttachments.hint')}>
      {files.error ? (
        <ErrorNotice error={files.error} />
      ) : (files.data ?? []).length === 0 ? (
        <p className="text-body text-default-600">{t('sampleAttachments.none')}</p>
      ) : (
        <ul className="flex flex-col gap-stack">
          {(files.data ?? []).map((file) => (
            <li key={file.id} className="flex flex-wrap items-start gap-stack border-s-2 border-default-200 ps-3">
              <div className="min-w-0">
                <p className="truncate text-body font-medium text-default-900" dir="auto">
                  {file.logicalName}
                </p>
                <p className="text-caption text-default-600">
                  {formatBytes(file.byteSize, { locale })}
                  {' · '}
                  {file.uploadedByName ?? t('common.notRecorded')}
                  {' · '}
                  {formatDateTime(file.createdAt, { locale })}
                </p>
                {file.description ? (
                  <p className="text-caption text-default-600">{file.description}</p>
                ) : null}
              </div>
              <div className="ms-auto flex items-center gap-inline">
                <FileDownloadButton
                  fileId={file.id}
                  fileName={file.logicalName}
                  fetchContent={() => sampleAttachmentsApi.download(sample.id, file.id)}
                />
                {mayRemove ? (
                  <ActionButton priority="tertiary" size="sm" onPress={() => setRemoving(file)}>
                    {t('common.remove')}
                  </ActionButton>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {mayAttach ? (
        <div className="mt-stack border-t border-default-100 pt-stack">
          <FileUploadField<StoredFile>
            label={t('sampleAttachments.attach')}
            upload={(file) => sampleAttachmentsApi.upload(sample.id, file)}
            onUploaded={() => void refresh()}
          />
        </div>
      ) : null}

      <Dialog
        isOpen={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('files.confirmRemoveTitle')}
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setRemoving(null)} isDisabled={remove.isPending}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={reason.trim().length < 3}
              isLoading={remove.isPending}
              onPress={() => remove.mutate()}
            >
              {t('common.remove')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          <p className="text-body text-default-700">{t('files.confirmRemoveBody')}</p>
          <TextAreaField label={t('common.reason')} value={reason} onChange={setReason} isRequired rows={2} />
          {remove.error ? <ErrorNotice error={remove.error} /> : null}
        </div>
      </Dialog>
    </Panel>
  );
}
